import { useState, useEffect } from 'react'
import {
  Eye, EyeOff, Plus, Trash2, RefreshCw, Loader2, ExternalLink,
  AlertTriangle, CheckCircle, XCircle, Clock, Calendar
} from 'lucide-react'
import api from '../../core/services/api'

interface LinkSorvegliato {
  id: string
  url: string
  titolo: string | null
  attivo: boolean
  sorveglia_da: string | null
  sorveglia_a: string | null
  intervallo_ore: number
  ultimo_check_at: string | null
  ultimo_status_code: number | null
  ultimo_errore: string | null
  check_count: number
  change_count: number
}

interface Props {
  fascicoloId: string
  onToast: (msg: string, type: 'success' | 'error') => void
}

export default function LinkSorvegliatiPanel({ fascicoloId, onToast }: Props) {
  const [links, setLinks] = useState<LinkSorvegliato[]>([])
  const [loading, setLoading] = useState(true)
  const [checking, setChecking] = useState<string | null>(null)

  // Form nuovo
  const [showForm, setShowForm] = useState(false)
  const [formUrl, setFormUrl] = useState('')
  const [formTitolo, setFormTitolo] = useState('')
  const [formDa, setFormDa] = useState('')
  const [formA, setFormA] = useState('')
  const [formIntervallo, setFormIntervallo] = useState(24)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadLinks()
  }, [fascicoloId])

  const loadLinks = async () => {
    try {
      const resp = await api.get(`/v1/fascicoli/${fascicoloId}/link-sorvegliati`)
      setLinks(resp.data)
    } catch {
      // Non mostrare errore se non ci sono link
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = async () => {
    if (!formUrl.trim()) return
    setSaving(true)
    try {
      await api.post(`/v1/fascicoli/${fascicoloId}/link-sorvegliati`, {
        fascicolo_id: fascicoloId,
        url: formUrl.trim(),
        titolo: formTitolo.trim() || null,
        sorveglia_da: formDa || null,
        sorveglia_a: formA || null,
        intervallo_ore: formIntervallo,
      })
      onToast('Sorveglianza attivata', 'success')
      setShowForm(false)
      setFormUrl('')
      setFormTitolo('')
      setFormDa('')
      setFormA('')
      setFormIntervallo(24)
      await loadLinks()
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (link: LinkSorvegliato) => {
    try {
      await api.put(`/v1/fascicoli/${fascicoloId}/link-sorvegliati/${link.id}`, {
        attivo: !link.attivo,
      })
      await loadLinks()
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore', 'error')
    }
  }

  const handleDelete = async (link: LinkSorvegliato) => {
    if (!confirm(`Rimuovere sorveglianza su "${link.titolo || link.url}"?`)) return
    try {
      await api.delete(`/v1/fascicoli/${fascicoloId}/link-sorvegliati/${link.id}`)
      onToast('Sorveglianza rimossa', 'success')
      await loadLinks()
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore', 'error')
    }
  }

  const handleCheckNow = async (link: LinkSorvegliato) => {
    setChecking(link.id)
    try {
      const resp = await api.post(`/v1/fascicoli/${fascicoloId}/link-sorvegliati/${link.id}/check-now`)
      if (resp.data.changed) {
        onToast('Aggiornamento rilevato!', 'success')
      } else if (resp.data.errore) {
        onToast(`Errore: ${resp.data.errore}`, 'error')
      } else {
        onToast('Nessun cambiamento rilevato', 'success')
      }
      await loadLinks()
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore check', 'error')
    } finally {
      setChecking(null)
    }
  }

  const formatDate = (d: string | null) => {
    if (!d) return '-'
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const formatDateTime = (d: string | null) => {
    if (!d) return 'Mai'
    return new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  }

  if (loading) return null

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Eye className="w-4 h-4" />
          Link Sorvegliati
          {links.length > 0 && (
            <span className="text-xs font-normal text-gray-400">({links.filter(l => l.attivo).length} attivi)</span>
          )}
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
        >
          <Plus className="w-3.5 h-3.5" />
          Sorveglia link
        </button>
      </div>

      {/* Form nuovo */}
      {showForm && (
        <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg space-y-2">
          <input
            type="url"
            value={formUrl}
            onChange={(e) => setFormUrl(e.target.value)}
            placeholder="https://www.regione.veneto.it/bando-xyz"
            className="w-full px-3 py-2 text-sm border rounded-lg"
          />
          <input
            type="text"
            value={formTitolo}
            onChange={(e) => setFormTitolo(e.target.value)}
            placeholder="Titolo (opzionale)"
            className="w-full px-3 py-1.5 text-sm border rounded-lg"
          />
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] text-gray-500">Da</label>
              <input type="date" value={formDa} onChange={(e) => setFormDa(e.target.value)}
                className="w-full px-2 py-1 text-xs border rounded" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500">A</label>
              <input type="date" value={formA} onChange={(e) => setFormA(e.target.value)}
                className="w-full px-2 py-1 text-xs border rounded" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500">Ogni (ore)</label>
              <select value={formIntervallo} onChange={(e) => setFormIntervallo(Number(e.target.value))}
                className="w-full px-2 py-1 text-xs border rounded">
                <option value={6}>6h</option>
                <option value={12}>12h</option>
                <option value={24}>24h (giornaliero)</option>
                <option value={48}>48h</option>
                <option value={168}>Settimanale</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdd} disabled={!formUrl.trim() || saving}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs hover:bg-primary-700 disabled:opacity-50">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
              Attiva sorveglianza
            </button>
            <button onClick={() => setShowForm(false)}
              className="px-3 py-1.5 border rounded-lg text-xs hover:bg-gray-50">
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      {links.length === 0 && !showForm ? (
        <p className="text-xs text-gray-400 text-center py-2">
          Nessun link sotto sorveglianza. Clicca "Sorveglia link" per iniziare.
        </p>
      ) : (
        <div className="space-y-2">
          {links.map(link => (
            <div
              key={link.id}
              className={`flex items-start gap-2 p-2.5 rounded-lg border text-xs ${
                link.attivo ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'
              }`}
            >
              {/* Status indicator */}
              <div className="mt-0.5 flex-shrink-0" title={
                link.ultimo_errore ? link.ultimo_errore
                : link.change_count > 0 ? `${link.change_count} aggiornamenti rilevati`
                : link.check_count > 0 ? 'Nessun cambiamento'
                : 'In attesa del primo check'
              }>
                {link.ultimo_errore ? (
                  <XCircle className="w-4 h-4 text-red-400" />
                ) : link.change_count > 0 ? (
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                ) : link.check_count > 0 ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <Clock className="w-4 h-4 text-gray-300" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-gray-900 truncate">
                    {link.titolo || link.url}
                  </span>
                  <a href={link.url} target="_blank" rel="noopener noreferrer"
                    className="text-primary-500 hover:text-primary-600 flex-shrink-0">
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="text-gray-400 mt-0.5 flex items-center gap-3">
                  <span>Check: {formatDateTime(link.ultimo_check_at)}</span>
                  <span>Cambiamenti: {link.change_count}</span>
                  {link.sorveglia_a && (
                    <span className="flex items-center gap-0.5">
                      <Calendar className="w-3 h-3" />
                      fino al {formatDate(link.sorveglia_a)}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => handleCheckNow(link)} disabled={!!checking}
                  className="p-1 text-gray-400 hover:text-primary-600 rounded" title="Controlla ora">
                  {checking === link.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => handleToggle(link)}
                  className="p-1 text-gray-400 hover:text-amber-600 rounded"
                  title={link.attivo ? 'Pausa' : 'Riattiva'}>
                  {link.attivo ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => handleDelete(link)}
                  className="p-1 text-gray-400 hover:text-red-600 rounded" title="Rimuovi">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
